import React, { useEffect } from 'react';
import { FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { cn } from '@utils/index';
import { ChevronLeft, ChevronDown, ChevronUp } from 'lucide-react';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@radix-ui/react-accordion';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { useInsuranceStore } from 'src/store/insurance';
import { useApplicationStore } from 'src/store/application';
import { usePlan } from '@features/onboarding/hooks/usePlan';
import { calculateCoverageLevel } from '@features/onboarding/utils/coverageLevel';
import { FormPlanDetails } from '@features/onboarding/components/shared/form-plan/FormPlanDetals';
import { ViewMode } from '@features/onboarding/components/shared/form-plan/MedicalPlansControls';
import { useTouchDevice } from '@features/onboarding/hooks/useTouchDevice';
import { InsurancePlan, MedicalPlan } from 'src/services/interfaces/insurance';
import { CriticalIllnessCoverageSelector } from './CriticalIllnessCoverageSelector';
import { useCompareStore } from 'src/store/compare';
import { format, parseISO } from 'date-fns';
import {
  getCriticalIllnessPriceId,
  PriceSearchConditions,
} from '@features/onboarding/utils/criticalIllnessPriceId';
import CompareIcon from '@components/shared/icon/CompareIcon';

interface FormPlanListProps<T extends FieldValues> {
  plans: MedicalPlan[];
  form: UseFormReturn<T>;
  name: Path<T>;
  viewMode: ViewMode;
  planLength: number;
  hideRadio?: boolean;
  expandedPlanIds: string[];
  onToggleExpand: (planId: string) => void;
  onOpenCompare?: () => void;
  planType?: string;
}

export const FormPlanList = <T extends FieldValues>({
  plans,
  form,
  name,
  viewMode,
  planLength,
  hideRadio,
  expandedPlanIds,
  onOpenCompare,
  onToggleExpand,
  planType = 'summary',
}: FormPlanListProps<T>) => {
  const allPlans = useInsuranceStore((state) => state.plans) as InsurancePlan[];
  const { setData } = useOnboardingStore();
  const aiHighlightedPlanUid = useOnboardingStore((s) => s.aiHighlightedPlanUid);
  const setAiHighlightedPlan = useOnboardingStore((s) => s.setAiHighlightedPlan);
  const { getPlanPrice } = usePlan();
  const { activeCategories, isMainPlanRejected, tier } = useApplicationStore();

  const isPlanBlocked = (plan: MedicalPlan) => {
    const fullPlan = allPlans.find((p) => p.uid === plan.id);
    if (!fullPlan) return false;

    if (fullPlan.category === 'Major Medical') {
      return isMainPlanRejected;
    } else {
      return activeCategories.includes(fullPlan.category);
    }
  };

  const getPlanInfo = (plan: InsurancePlan, planUid: string) => {
    let coverageLevel: number;
    if (plan.category === 'Major Medical') {
      const medicalTags =
        (form.watch('medicalTags' as Path<T>) as unknown as string[]) || [];
      coverageLevel = calculateCoverageLevel(medicalTags);
    } else {
      const planTags =
        (form.watch('planTags' as Path<T>) as unknown as Record<
          string,
          string[]
        >) || {};
      const planTagsForThisPlan = planTags[planUid] || [];
      coverageLevel = calculateCoverageLevel(planTagsForThisPlan);
    }

    let selectedTier: string | undefined;
    let selectedCoverageAmount: number | undefined;

    if (plan.category === 'Major Medical') {
      if (form.watch('medicalPlanTier' as Path<T>)) {
        selectedTier = form.watch(
          'medicalPlanTier' as Path<T>
        ) as unknown as string;
      }
      if (form.watch('medicalPlanCoverageAmount' as Path<T>)) {
        selectedCoverageAmount = form.watch(
          'medicalPlanCoverageAmount' as Path<T>
        ) as unknown as number;
      }
    } else if (plan.category === 'Dental') {
      if (form.watch('dentalPlanTier' as Path<T>)) {
        selectedTier = form.watch(
          'dentalPlanTier' as Path<T>
        ) as unknown as string;
      }
      if (form.watch('dentalPlanCoverageAmount' as Path<T>)) {
        selectedCoverageAmount = form.watch(
          'dentalPlanCoverageAmount' as Path<T>
        ) as unknown as number;
      }
    } else if (plan.category === 'Critical Illness') {
      const sanitizeCategory = (category: string) =>
        category.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
      const criticalIllnessPlanKey = `${sanitizeCategory(plan.category)}Plan`;
      const coverageAmountKey = `${criticalIllnessPlanKey.replace('Plan', 'PlanCoverageAmount')}`;
      const coverageAmount = form.watch(
        coverageAmountKey as Path<T>
      ) as unknown as number | undefined;
      if (coverageAmount) {
        selectedCoverageAmount = coverageAmount;
      } else {
        selectedCoverageAmount = 10000;
      }

      const selectedPlanUid = form.watch(name as Path<T>) as unknown as
        | string
        | undefined;
      if (selectedPlanUid === planUid) {
        const storeData = useOnboardingStore.getState().data;
        const storeCoverageAmount = (storeData as Record<string, unknown>)[
          coverageAmountKey
        ] as number | undefined;
        if (storeCoverageAmount) {
          selectedCoverageAmount = storeCoverageAmount;
        }
      } else {
        selectedCoverageAmount = 10000;
      }
    }

    const price = getPlanPrice(
      plan,
      coverageLevel,
      selectedTier,
      selectedCoverageAmount
    );

    const result = {
      coverageLevel,
      selectedTier,
      selectedCoverageAmount,
      price,
    };
    return result;
  };

  const getDisplayTier = React.useCallback(
    (planUid: string): string | null => {
      const tierFieldName = name.replace('Plan', 'PlanTier');
      const currentTier = form.watch(tierFieldName as Path<T>) as unknown as
        | string
        | undefined;
      if (currentTier) return currentTier;
      const planObj = allPlans.find((p) => p.uid === planUid);
      if (!planObj) return null;
      return planObj.category === 'Major Medical' ? tier : null;
    },
    [form, name, allPlans, tier]
  );

  const selectedPlan = form.watch(name);

  const sanitizeCategory = (category: string) =>
    category.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  const criticalIllnessPlanKey = `${sanitizeCategory('Critical Illness')}Plan`;
  const criticalIllnessCoverageAmountKey = `${criticalIllnessPlanKey.replace('Plan', 'PlanCoverageAmount')}`;

  const isTouchDevice = useTouchDevice();
  const [isSmallScreen, setIsSmallScreen] = React.useState(
    window.innerWidth < 1400
  );

  React.useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 1400);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const comparisonLimit = isTouchDevice || isSmallScreen ? 2 : 3;

  const truncate = (text: string, maxChars: number) => {
    return text.length > maxChars ? text.slice(0, maxChars) + '...' : text;
  };

  const handleSelectPlan = (id: string) => {
    const selected = plans.find((plan) => plan.id === id);
    if (!selected) return;

    if (isPlanBlocked(selected)) return;

    const fieldName = name.replace('Plan', 'PlanPrice');
    const tierFieldName = name.replace('Plan', 'PlanTier');
    const coverageAmountFieldName = name.replace('Plan', 'PlanCoverageAmount');

    if (selectedPlan === id) {
      const resetData: Record<string, unknown> = {
        [name]: '',
        [fieldName]: 0,
        [tierFieldName]: '',
      };

      const plan = allPlans.find((p) => p.uid === id);
      if (plan?.category === 'Critical Illness') {
        resetData[coverageAmountFieldName] = 10000;
        form.setValue(
          coverageAmountFieldName as Path<T>,
          10000 as unknown as T[Path<T>]
        );

        let primaryAge = 30;
        const applicants = (form.watch('applicants' as Path<T>) as any[]) || [];
        if (applicants.length > 0) {
          const primaryApplicant = applicants.find(
            (app) => app.applicant === 'primary'
          );
          if (primaryApplicant && primaryApplicant.dateOfBirth) {
            const birthYear = parseInt(primaryApplicant.dateOfBirth.year);
            const currentYear = parseISO(format(new Date(), 'yyyy-MM-dd')).getFullYear();
            primaryAge = currentYear - birthYear;
          }
        }

        const planTagsObj =
          (form.watch('planTags' as Path<T>) as unknown as Record<
            string,
            string[]
          >) || {};
        const planTagsForThisPlan = planTagsObj[plan.uid] || [];

        const dependentsCount = planTagsForThisPlan.filter((tag: string) =>
          tag.includes('dependent')
        ).length;
        const hasSpouse = planTagsForThisPlan.some((tag: string) =>
          tag.includes('spouse')
        );

        const conditions: PriceSearchConditions = {
          coverageAmount: 10000,
          primaryAge,
          dependentsCount,
          hasSpouse,
        };

        const priceId = getCriticalIllnessPriceId(plan, conditions);
        const validPrice = plan.prices.find((price) => price.id === priceId);
        const resetPrice = validPrice?.price || 0;

        resetData[fieldName] = resetPrice;
        form.setValue(
          fieldName as Path<T>,
          resetPrice as unknown as T[Path<T>]
        );
      }

      form.setValue(name as Path<T>, '' as unknown as T[Path<T>]);
      // Сбрасываем подсветку при ручном выборе
      setAiHighlightedPlan(null);
      setData(resetData);
    } else {
      form.setValue(name as Path<T>, id as unknown as T[Path<T>]);
      const fullPlan = allPlans.find((p) => p.uid === id);
      let selectedTier: string | null = null;
      let selectedCoverageAmount: number | null = null;
      let priceNumber = 0;

      if (fullPlan && fullPlan.prices && fullPlan.prices.length > 0) {
        const availableTiers = [
          ...new Set(fullPlan.prices.map((p) => p.tier).filter(Boolean)),
        ];
        if (availableTiers.length > 0) {
          selectedTier =
            fullPlan.category === 'Major Medical'
              ? availableTiers.find((t) => t === tier) ||
                availableTiers.find((t) => t === 'Tier 1') ||
                availableTiers[0]
              : availableTiers.find((t) => t === 'Tier 1') ||
                availableTiers[0];
        }
      }

      if (fullPlan?.category === 'Critical Illness') {
        const currentCoverage = form.getValues(
          coverageAmountFieldName as Path<T>
        ) as unknown as number | null;
        selectedCoverageAmount =
          currentCoverage && currentCoverage !== 0 ? currentCoverage : 10000;

        let primaryAge = 30;
        const applicants = (form.watch('applicants' as Path<T>) as any[]) || [];
        if (applicants.length > 0) {
          const primaryApplicant = applicants.find(
            (app) => app.applicant === 'primary'
          );
          if (primaryApplicant && primaryApplicant.dateOfBirth) {
            const birthYear = parseInt(primaryApplicant.dateOfBirth.year);
            const currentYear = parseISO(format(new Date(), 'yyyy-MM-dd')).getFullYear();
            primaryAge = currentYear - birthYear;
          }
        }

        const planTagsObj =
          (form.watch('planTags' as Path<T>) as unknown as Record<
            string,
            string[]
          >) || {};
        const planTagsForThisPlan = planTagsObj[fullPlan.uid] || [];

        const dependentsCount = planTagsForThisPlan.filter((tag: string) =>
          tag.includes('dependent')
        ).length;
        const hasSpouse = planTagsForThisPlan.some((tag: string) =>
          tag.includes('spouse')
        );

        const conditions: PriceSearchConditions = {
          coverageAmount: selectedCoverageAmount,
          primaryAge,
          dependentsCount,
          hasSpouse,
        };

        const priceId = getCriticalIllnessPriceId(fullPlan, conditions);
        const validPrice = fullPlan.prices.find(
          (price) => price.id === priceId
        );
        priceNumber = validPrice?.price || 0;
      } else {
        const priceObj = fullPlan?.prices.find(
          (p) => p.coverage === 1 && (!selectedTier || p.tier === selectedTier)
        );
        priceNumber = priceObj?.price || 0;
      }

      const updateData: Record<string, unknown> = {
        [name]: id,
        [fieldName]: priceNumber,
        [tierFieldName]: selectedTier,
      };

      if (selectedCoverageAmount !== null) {
        updateData[coverageAmountFieldName] = selectedCoverageAmount;
        form.setValue(
          coverageAmountFieldName as Path<T>,
          selectedCoverageAmount as unknown as T[Path<T>]
        );
      }

      // Сбрасываем подсветку при ручном выборе
      setAiHighlightedPlan(null);
      setData(updateData);
    }
  };

  const [expandedDescriptions, setExpandedDescriptions] = React.useState<
    string[]
  >([]);
  const toggleDescription = (id: string) => {
    setExpandedDescriptions((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id]
    );
  };
  const isDescriptionExpanded = (id: string) =>
    expandedDescriptions.includes(id);

  const { togglePlan, selectedPlans } = useCompareStore();

  const handleSelectCompare = (id: string) => {
    togglePlan(id);
  };

  const getFormattedPrice = (plan: MedicalPlan, allPlans: InsurancePlan[]) => {
    const fullPlan = allPlans.find((p) => p.uid === plan.id);
    if (!fullPlan) {
      return plan.price;
    }

    if (fullPlan.category === 'Critical Illness') {
      const planInfo = getPlanInfo(fullPlan, plan.id);
      const formattedPrice = `$${planInfo.price.toFixed(2)}`;
      return formattedPrice;
    }

    const tier = getDisplayTier(plan.id);

    let coverageLevel = 1;
    if (fullPlan.category === 'Major Medical') {
      const medicalTags =
        (form.watch('medicalTags' as Path<T>) as unknown as string[]) || [];
      coverageLevel = calculateCoverageLevel(medicalTags);
    } else {
      const planTagsObj =
        (form.watch('planTags' as Path<T>) as unknown as Record<
          string,
          string[]
        >) || {};
      const tags = planTagsObj[plan.id] || [];
      coverageLevel = calculateCoverageLevel(tags);
    }

    if (tier) {
      const entry = fullPlan.prices?.find(
        (e) => e.tier === tier && e.coverage === coverageLevel
      );
      if (entry) {
        return `$${entry.price.toFixed(2)}`;
      }
    }

    const planInfo = getPlanInfo(fullPlan, plan.id);
    const formattedPrice = `$${planInfo.price.toFixed(2)}`;
    return formattedPrice;
  };

  useEffect(() => {
    if (aiHighlightedPlanUid) {
     onToggleExpand(aiHighlightedPlanUid);
    }
  }, [aiHighlightedPlanUid]);

  if (viewMode === 'tab') {
    return (
      <div className="overflow-x-auto mt-6.5">
        <table className="w-full border-collapse text-[14px] md:text-[14px]">
          <thead className="text-left text-black border-b text-[12px]">
            <tr>
              <th className="py-2"></th>
              <th className="py-2 max-lg:hidden">Insurer</th>
              <th className="py-2">Plan Name</th>
              <th className="py-2 max-md:hidden">Description</th>
              <th className="py-2">Monthly Premium</th>
              <th className="py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {plans.map((plan, index) => {
              const isSelected = selectedPlan === plan.id;
              const isCompare = selectedPlans.includes(plan.id);
              const uniqueKey = `${planType}-${plan.id}-${index}`;
              const isComparisonLimitReached =
                selectedPlans.length >= comparisonLimit && !isCompare;

              return (
                <React.Fragment key={uniqueKey}>
                  <tr className="border-b text-[12px] md:text-[12px] align-center">
                    <td className="py-3 pl-2 pr-1">
                      {!hideRadio && (
                        <button
                          type="button"
                          onClick={() => handleSelectPlan(plan.id)}
                          className={cn(
                            'px-3 py-1 rounded-full text-xs font-semibold cursor-pointer',
                            isSelected
                              ? 'bg-primary text-white'
                              : 'bg-[#f0eee9] text-black hover:bg-[#e4e2db]'
                          )}
                        >
                          {isSelected ? 'Selected' : 'Select'}
                        </button>
                      )}
                    </td>
                    <td className="py-3 max-lg:hidden">Cigna</td>
                    <td className="py-3">{plan.title}</td>
                    <td className="py-3 max-md:hidden">
                      {truncate(plan.description, 31)}
                    </td>
                    <td className="py-3">
                      {getFormattedPrice(plan, allPlans)}
                    </td>
                    <td className="py-3 pr-2">
                      <div className="flex items-center align-center gap-3">
                        {planLength > 1 && !isPlanBlocked(plan) && (
                          <button
                            title="Compare plan"
                            type="button"
                            onClick={() => handleSelectCompare(plan.id)}
                            disabled={isComparisonLimitReached}
                            className={`text-sm align-center cursor-pointer hover:text-red-800 ${
                              isCompare
                                ? 'text-red-600 font-semibold'
                                : 'text-black'
                            } ${isComparisonLimitReached ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <CompareIcon />
                          </button>
                        )}
                        <button
                          type="button"
                          title={
                            expandedPlanIds.includes(plan.id)
                              ? 'Hide details'
                              : 'Show details'
                          }
                          onClick={() => onToggleExpand(plan.id)}
                          className="ml-auto p-0 text-black cursor-pointer hover:text-red-800"
                          aria-label={
                            expandedPlanIds.includes(plan.id)
                              ? 'Hide details'
                              : 'Show details'
                          }
                        >
                          <div className="flex flex-col gap-1">
                            <span className="w-4 h-0.5 bg-primary rounded-lg"/>
                            <span className="w-4 h-0.5 bg-primary rounded-lg"/>
                            <span className="w-4 h-0.5 bg-primary rounded-lg"/>
                          </div>
                        </button>
                      </div>
                    </td>
                  </tr>
                  {allPlans.find((p) => p.uid === plan.id)?.category ===
                    'Critical Illness' && (
                    <tr className="bg-white">
                      <td colSpan={6}>
                        <CriticalIllnessCoverageSelector
                          planId={plan.id}
                          plan={
                            allPlans.find(
                              (p) => p.uid === plan.id
                            ) as InsurancePlan
                          }
                          form={form}
                          name={name}
                          viewMode={viewMode}
                        />
                      </td>
                    </tr>
                  )}
                  {expandedPlanIds.includes(plan.id) && (
                    <tr className="bg-white">
                      <td colSpan={6} className="p-4 border-b border-gray-200">
                        <FormPlanDetails
                          plan={
                            allPlans.find(
                              (e) => e.uid === plan.id
                            ) as InsurancePlan
                          }
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex flex-col gap-4',
        viewMode === 'grid' ? 'md:grid md:grid-cols-2 items-start' : ''
      )}
    >
      {plans.map((plan, index) => {
        const isSelected = selectedPlan === plan.id;
        const isCompare = selectedPlans.includes(plan.id);
        const uniqueKey = `${planType}-${plan.id}-${index}-article`;
        const isComparisonLimitReached =
          selectedPlans.length >= comparisonLimit && !isCompare;

        const fullPlan = allPlans.find((p) => p.uid === plan.id);

        return (
          <article
            key={uniqueKey}
            className={cn(
              'bg-transparent flex flex-col rounded-xl overflow-hidden border-2 px-[13px] relative transition-all',
              viewMode !== 'grid' && 'mt-4',
              viewMode === 'grid' && index < 2 && 'mt-4',
              isSelected
                ? 'border-primary plan-selected-shadow'
                : aiHighlightedPlanUid === plan.id
                  ? 'border-[rgba(113,0,54,.75)]'
                  : 'border-transparent shadow'
            )}
          >
            <div className="absolute top-4 right-4">
              {!hideRadio && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectPlan(plan.id);
                  }}
                  className={cn(
                    'px-3 py-1 rounded-sm border border-primary text-xs font-semibold cursor-pointer !font-secondary transition-all hover:bg-primary/90',
                    isSelected
                      ? 'bg-primary text-white'
                      : 'bg-white text-primary hover:text-white'
                  )}
                >
                  {isSelected ? 'Selected' : 'Select'}
                </button>
              )}
            </div>
            <header
              onClick={() => handleSelectPlan(plan.id)}
              className={cn(
                'flex md:flex-row flex-col justify-between md:items-center gap-4 py-4',
                !hideRadio && 'cursor-pointer'
              )}
            >
              <div className="flex flex-row items-center gap-2 2xl:gap-4 md:gap-10">
                <img
                  src={plan.image}
                  alt={plan.title}
                  className="xl:size-18 size-16 object-contain mb-auto"
                />
                <div>
                  <h4
                    className={cn(
                      'xl:text-xl text-base font-extrabold leading-4.5 lg:leading-6 mb-3 text-inherit pr-9 max-sm:pr-8 max-2xs:pr-24'
                    )}
                  >
                    {plan.title}
                  </h4>
                  <p
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleDescription(plan.id);
                    }}
                    className={cn(
                      'text-xs font-normal !font-secondary text-inherit max-w-md cursor-pointer transition-all duration-200',
                      viewMode === 'grid' &&
                        !isDescriptionExpanded(plan.id) &&
                        'line-clamp-2'
                    )}
                    title="Click to expand"
                  >
                    {plan.description}
                  </p>
                </div>
              </div>
            </header>
            {(() => {
              const isCriticalIllness =
                fullPlan?.category === 'Critical Illness';

              return isCriticalIllness && isSelected ? (
                <CriticalIllnessCoverageSelector
                  planId={plan.id}
                  plan={fullPlan as InsurancePlan}
                  form={form}
                  name={name}
                  viewMode={viewMode}
                />
              ) : null;
            })()}
            <footer className="text-black mt-auto">
              <Accordion
                type="multiple"
                value={expandedPlanIds.includes(plan.id) ? ['item-1'] : []}
                onValueChange={() => onToggleExpand(plan.id)}
              >
                <AccordionItem value="item-1" className="flex flex-col gap-2">
                  <div
                    className={cn(
                      'flex flex-col border-t border-gray-200 cursor-pointer',
                      expandedPlanIds.includes(plan.id) && 'border-b'
                    )}
                  >
                    <AccordionTrigger
                      className="flex xl:py-2 py-2 justify-between items-center w-full text-xs font-semibold p-0 cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span>
                        {expandedPlanIds.includes(plan.id)
                          ? 'Hide plan detail'
                          : 'Show plan details'}
                      </span>
                      {expandedPlanIds.includes(plan.id) ? (
                        <ChevronUp />
                      ) : (
                        <ChevronDown />
                      )}
                    </AccordionTrigger>
                  </div>
                  <AccordionContent className="bg-white text-black">
                    <FormPlanDetails
                      plan={
                        allPlans.find((e) => e.uid === plan.id) as InsurancePlan
                      }
                    />
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
              <div className="!font-secondary pt-3 pb-4 flex justify-between items-center max-md:flex-col max-md:items-start max-md:gap-8 border-t border-gray-200">
                <p className="xl:text-base text-sm font-semibold flex items-center gap-2">
                  <span className="text-black text-xs font-bold inline-flex items-end h-6 text-nowrap">
                    As low as:
                  </span>
                  <span className="text-black text-2xl font-bold">
                    {getFormattedPrice(plan, allPlans)}
                  </span>
                  <span className="text-xs 3xl:text-sm font-normal text-[#777775] !font-secondary">
                    / {plan.period}
                  </span>
                </p>
                {planLength > 1 && !isPlanBlocked(plan) && (
                  <button
                    type="button"
                    onClick={(e) => {
                      if (onOpenCompare && planLength === 2) {
                        onOpenCompare();
                      } else {
                        e.stopPropagation();
                        handleSelectCompare(plan.id);
                      }
                    }}
                    disabled={isComparisonLimitReached}
                    className={`cursor-pointer text-xs 3xl:text-sm font-bold ${
                      isCompare ? 'text-destructive' : ''
                    } ${isComparisonLimitReached ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {isCompare ?
                      'Remove from compare' : 
                      planLength === 2 ?
                        'Compare' : 'Add to compare'
                    }
                  </button>
                )}
              </div>
            </footer>
          </article>
        );
      })}
    </div>
  );
};
